import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Field, Button, Alert, Spinner } from "@/components/ui";
import { Logo } from "@/components/Logo";
import { Check, ArrowLeft } from "lucide-react";
import { captureAttribution, getAttribution, getAdConsent, trackSignupConversion } from "@/lib/attribution";
import { captureEvent } from "@/lib/analytics";

const money = (cents) => `$${Math.round((cents || 0) / 100)}`;

export default function Signup() {
  const navigate = useNavigate();
  const { signUp, refresh } = useAuth();
  const [params] = useSearchParams();
  const urlPromo = params.get("promo");

  const [step, setStep] = useState(1);
  const [plans, setPlans] = useState(null);
  const [trialDays, setTrialDays] = useState(14);
  const [selected, setSelected] = useState(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // Promo code (Step 1). The discount + "Free" label are sourced from the server
  // (never client state), so we never promise $0 while Stripe would charge.
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoBusy, setPromoBusy] = useState(false);
  const [promoErr, setPromoErr] = useState(null);

  const applyPromo = async (raw) => {
    const code = (raw ?? promoInput).trim().toUpperCase();
    if (!code) return;
    setPromoErr(null); setPromoBusy(true);
    try {
      const r = await api.promoPreview(code, selected || "basic");
      if (r?.valid) {
        setAppliedPromo(r);
        setPromoInput(r.code);
      } else {
        setAppliedPromo(null);
        setPromoErr(r?.description || "That promo code isn't valid.");
      }
    } catch {
      setAppliedPromo(null);
      setPromoErr("Promo codes aren't available right now. Please try again shortly.");
    } finally { setPromoBusy(false); }
  };

  const clearPromo = () => { setAppliedPromo(null); setPromoInput(""); setPromoErr(null); };

  const promoFree = !!appliedPromo && appliedPromo.amount_due_cents === 0;

  useEffect(() => {
    captureAttribution();
    api.billingPlans()
      .then((r) => { setPlans(r.plans || []); if (r.trial_days) setTrialDays(r.trial_days); })
      .catch(() => setPlans([]));
  }, []);

  useEffect(() => {
    captureEvent("signup_view", { promo_in_url: !!urlPromo });
  }, []);

  useEffect(() => {
    if (urlPromo && !appliedPromo) applyPromo(urlPromo);
  }, [urlPromo, plans]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null); setBusy(true);
    try {
      // ad_consent is a type-checked JSON boolean on apex — include it ONLY when
      // we have a real signal (kb_ad_consent cookie); otherwise omit it.
      const attribution = { ...getAttribution(), plan: selected };
      const adConsent = getAdConsent();
      if (adConsent !== undefined) attribution.ad_consent = adConsent;
      await signUp(email.trim().toLowerCase(), password, name.trim(), attribution);
      captureEvent("sign_up", { plan: selected, promo_applied: !!appliedPromo, promo_free: promoFree });
      // Account now exists (201). Fire the B2C Pilot Signup conversion once —
      // before any Stripe redirect, so paid signups are counted too.
      trackSignupConversion();
      // Create a minimal family so a (paid) subscription can attach before onboarding.
      await api.createFamily({ name: `${name.trim()}'s Family` });
      try { await refresh(); } catch { /* Onboarding re-syncs on mount */ }
      if (selected === "free") {
        navigate("/signup/success", { replace: true });
        return;
      }
      const res = await api.billingCheckout(selected, appliedPromo?.code || null);
      if (res?.url) {
        captureEvent("start_trial", { plan: selected, promo_applied: !!appliedPromo, via: "stripe_checkout" });
        window.location.href = res.url; return;
      }
      // Sandbox / no-Stripe: subscription activated locally.
      try { await refresh(); } catch { /* Onboarding re-syncs on mount */ }
      captureEvent("start_trial", { plan: selected, promo_applied: !!appliedPromo, via: "local" });
      navigate("/signup/success", { replace: true });
    } catch (ex) {
      captureEvent("signup_failed", { plan: selected, reason: ex?.code || ex?.status || "error" });
      setErr(ex?.message || "Signup failed");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface p-6">
      <div className="w-full max-w-md">
        <Logo className="h-9" testid="brand-name" />

        {step === 1 && (
          <div className="kb-fade-up">
            <div className="text-xs font-semibold text-brand uppercase tracking-wide mt-5">Step 1 of 2 · Choose a plan</div>
            <h1 className="font-display text-3xl text-ink mt-2 mb-1">Start your care circle</h1>
            <p className="text-sm text-subtle mb-6">Pick the plan that fits. Paid plans include a {trialDays}-day free trial — cancel anytime.</p>

            {!plans ? <Spinner /> : (
              <div className="space-y-3">
                {plans.map((p) => {
                  const isSel = selected === p.id;
                  const paid = (p.amount_cents || 0) > 0;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`plan-${p.id}`}
                      onClick={() => setSelected(p.id)}
                      className={`w-full text-left rounded-2xl border p-4 transition-colors ${isSel ? "border-brand bg-brand-tint" : "border-line bg-white hover:border-brand-dark/40"}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-display text-lg text-ink">{p.name}</span>
                        {paid && promoFree ? (
                          <span data-testid={`plan-price-${p.id}`} className="flex items-center gap-1.5">
                            <span className="text-subtle line-through">{`${money(p.amount_cents)}/mo`}</span>
                            <span className="text-mint-dark font-bold">{money(appliedPromo.amount_due_cents)}</span>
                          </span>
                        ) : (
                          <span data-testid={`plan-price-${p.id}`} className="text-ink font-semibold">{paid ? `${money(p.amount_cents)}/mo` : "Free"}</span>
                        )}
                      </div>
                      <div className="text-sm text-subtle mt-0.5">{p.tagline}</div>
                      {paid && promoFree && (
                        <div className="text-xs font-semibold text-mint-dark mt-1">{appliedPromo.label}</div>
                      )}
                      {isSel && <div className="mt-2 inline-flex items-center gap-1 text-xs font-bold text-brand"><Check className="w-3.5 h-3.5" /> Selected</div>}
                    </button>
                  );
                })}
              </div>
            )}

            <div className="mt-5" data-testid="promo-section">
              <label className="text-sm font-semibold text-ink">Have a promo code?</label>
              {appliedPromo ? (
                <div data-testid="promo-applied" className="mt-2 flex items-center justify-between rounded-xl border border-mint-dark bg-mint-tint px-3 py-2.5">
                  <span className="flex items-center gap-2 text-sm text-ink">
                    <Check className="w-4 h-4 text-mint-dark" />
                    <span><span className="font-bold">{appliedPromo.code}</span> applied — {appliedPromo.label || appliedPromo.description}</span>
                  </span>
                  <button type="button" data-testid="promo-remove" onClick={clearPromo} className="text-xs font-semibold text-brand hover:underline">Remove</button>
                </div>
              ) : (
                <div className="mt-2 flex gap-2">
                  <input
                    data-testid="promo-input"
                    value={promoInput}
                    onChange={(e) => { setPromoInput(e.target.value.toUpperCase()); setPromoErr(null); }}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyPromo(); } }}
                    placeholder="PILOT2026"
                    className="flex-1 rounded-xl border border-line bg-white px-3 py-2.5 text-sm text-ink uppercase placeholder:normal-case placeholder:text-subtle focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                  />
                  <button
                    type="button"
                    data-testid="promo-apply"
                    onClick={() => applyPromo()}
                    disabled={promoBusy || !promoInput.trim()}
                    className="rounded-xl bg-brand px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-dark disabled:opacity-50"
                  >
                    {promoBusy ? "…" : "Apply"}
                  </button>
                </div>
              )}
              {promoErr && <div data-testid="promo-error" className="mt-1.5 text-xs text-danger">{promoErr}</div>}
            </div>

            <Button data-testid="plan-continue" onClick={() => { if (selected) { captureEvent("signup_start", { plan: selected, promo_applied: !!appliedPromo }); setStep(2); } }} disabled={!selected} className="w-full mt-5">
              Continue
            </Button>
            <p className="text-sm text-subtle text-center mt-6">
              Have an account?{" "}
              <Link to="/login" data-testid="go-to-login-link" className="text-brand font-semibold hover:underline">Sign in</Link>
            </p>
          </div>
        )}

        {step === 2 && (
          <form onSubmit={submit} className="kb-fade-up">
            <button type="button" data-testid="signup-back" onClick={() => setStep(1)} className="inline-flex items-center gap-1 text-sm text-subtle hover:text-ink mt-5">
              <ArrowLeft className="w-4 h-4" /> Back to plans
            </button>
            <div className="text-xs font-semibold text-brand uppercase tracking-wide mt-3">Step 2 of 2 · Create your account</div>
            <h1 className="font-display text-3xl text-ink mt-2 mb-1">Create your account</h1>
            <p className="text-sm text-subtle mb-2">
              Selected plan: <span className="font-semibold text-ink" data-testid="selected-plan-name">{plans?.find((p) => p.id === selected)?.name}</span>{" "}
              <button type="button" onClick={() => setStep(1)} className="text-brand font-semibold hover:underline">change</button>
            </p>
            <p className="text-sm text-subtle mb-6">
              {selected === "free"
                ? "You'll set up your care recipient next."
                : promoFree
                  ? `${appliedPromo.label} You'll set up your care recipient next.`
                  : `Next you'll enter payment to start your ${trialDays}-day free trial.`}
            </p>

            <Field label="Your name" data-testid="signup-name-input" value={name}
              onChange={(e) => setName(e.target.value)} placeholder="Alex Rivera" />
            <Field label="Email" type="email" data-testid="signup-email-input" value={email}
              onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" autoComplete="email" />
            <Field label="Password" type="password" data-testid="signup-password-input" value={password}
              onChange={(e) => setPassword(e.target.value)} placeholder="At least 8 characters" autoComplete="new-password" />
            <Alert testid="signup-error">{err}</Alert>

            <Button type="submit" data-testid="signup-submit-button" loading={busy}
              disabled={!name.trim() || !email.trim() || password.length < 8} className="w-full mt-2">
              {selected === "free" || promoFree ? "Create account" : "Continue to payment"}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
